
import { supabase } from '@/integrations/supabase/client';

/**
 * Source name map for all entity types
 */
export interface SourceNameMap {
  farms: Map<string, string>;
  suppliers: Map<string, string>;
  agents: Map<string, string>;
}

/**
 * Fetch all source names (farms, suppliers, agents) for enrichment
 * Fetches minimal columns (id, name) only - no select('*')
 * Uses Promise.all for batched queries
 */
export async function fetchAllSourceNames(): Promise<SourceNameMap> {
  const [farmsResult, suppliersResult, agentsResult] = await Promise.all([
    supabase.from('farms').select('id, farmer_name'),
    supabase.from('suppliers').select('id, name'),
    supabase.from('agents').select('id, name'),
  ]);

  const farms = new Map<string, string>();
  const suppliers = new Map<string, string>();
  const agents = new Map<string, string>();

  (farmsResult.data || []).forEach((f) => {
    farms.set(f.id, f.farmer_name);
  });

  (suppliersResult.data || []).forEach((s) => {
    suppliers.set(s.id, s.name);
  });

  (agentsResult.data || []).forEach((a) => {
    agents.set(a.id, a.name);
  });

  return { farms, suppliers, agents };
}

/**
 * Get source name by type and ID from source name map
 */
export function getSourceNameById(
  sourceType: string,
  sourceId: string | null,
  sourceNameMap: SourceNameMap
): string | null {
  if (!sourceId) return null;

  switch (sourceType) {
    case 'farm':
      return sourceNameMap.farms.get(sourceId) || null;
    case 'supplier':
      return sourceNameMap.suppliers.get(sourceId) || null;
    case 'agent':
      return sourceNameMap.agents.get(sourceId) || null;
    default:
      return null;
  }
}

/**
 * Enrich procurement with detailed data (items, costs, finance, warehouse, requestor)
 * Note: This function makes multiple DB calls. For bulk operations, use optimized hooks with JOINs.
 */
export const enrichProcurementWithDetails = async (procurement: any) => {

  // Fetch items with nested quality and warehouse data
  const { data: items, error: itemsError } = await supabase
    .from('procurement_items')
    .select(`
      *,
      item_quality:procurement_item_quality(*),
      item_warehouse:procurement_item_warehouse(*)
    `)
    .eq('procurement_id', procurement.id);

  if (itemsError) {
    console.error('Error fetching procurement items:', itemsError);
  }

  // Transform items to extract single item_quality/item_warehouse objects from arrays
  const enrichedItems = items?.map(item => ({
    ...item,
    item_quality: Array.isArray(item.item_quality) ? item.item_quality[0] : item.item_quality,
    item_warehouse: Array.isArray(item.item_warehouse) ? item.item_warehouse[0] : item.item_warehouse,
  })) || [];

  // Fetch costs
  const { data: costs, error: costsError } = await supabase
    .from('procurement_costs')
    .select('*')
    .eq('procurement_id', procurement.id)
    .maybeSingle();

  if (costsError) {
    console.error('Error fetching procurement costs:', costsError);
  }

  // Fetch finance
  const { data: finance, error: financeError } = await supabase
    .from('procurement_finance')
    .select('*')
    .eq('procurement_id', procurement.id)
    .maybeSingle();

  if (financeError) {
    console.error('Error fetching procurement finance:', financeError);
  }

  // Fetch warehouse
  const { data: warehouse, error: warehouseError } = await supabase
    .from('procurement_warehouse')
    .select('*')
    .eq('procurement_id', procurement.id)
    .maybeSingle();

  if (warehouseError) {
    console.error('Error fetching procurement warehouse:', warehouseError);
  }

  // Fetch quality
  const { data: quality, error: qualityError } = await supabase
    .from('procurement_quality')
    .select('*')
    .eq('procurement_id', procurement.id)
    .maybeSingle();

  if (qualityError) {
    console.error('Error fetching procurement quality:', qualityError);
  }

  // Fetch requestor details
  let requestor = null;
  if (procurement.requestor_id) {
    const { data: requestorData, error: requestorError } = await supabase
      .from('user_profiles')
      .select('id, first_name, last_name, role')
      .eq('id', procurement.requestor_id)
      .maybeSingle();

    if (!requestorError && requestorData) {
      requestor = requestorData;
    }
  }

  // Fetch source name based on source type
  let source_name = null;
  if (procurement.source_type === 'supplier' && procurement.source_id) {
    const { data: supplierData } = await supabase
      .from('suppliers')
      .select('name')
      .eq('id', procurement.source_id)
      .maybeSingle();
    source_name = supplierData?.name;
  } else if (procurement.source_type === 'agent' && procurement.source_id) {
    const { data: agentData } = await supabase
      .from('agents')
      .select('name')
      .eq('id', procurement.source_id)
      .maybeSingle();
    source_name = agentData?.name;
  } else if (procurement.source_type === 'farm' && procurement.source_id) {
    const { data: farmData } = await supabase
      .from('farms')
      .select('farmer_name')
      .eq('id', procurement.source_id)
      .maybeSingle();
    source_name = farmData?.farmer_name;
  }

  // Calculate total quantity if not already set
  let calculatedTotalQuantity = procurement.total_quantity;
  if (items && items.length > 0 && (!calculatedTotalQuantity || calculatedTotalQuantity === 0)) {
    calculatedTotalQuantity = items.reduce((total, item) => total + (item.quantity || 0), 0);
  }

  const enriched = {
    ...procurement,
    items: enrichedItems,
    costs,
    finance,
    warehouse,
    quality,
    requestor,
    source_name,
    total_quantity: calculatedTotalQuantity || procurement.total_quantity || 0
  };

  return enriched;
};
